/**
 * Notification Popup (Fixed: Event Delegation)
 * - Uses Event Delegation to guarantee form interception.
 * - Prevents redirection to homepage.
 */
class NotificationPopup extends HTMLElement {
  connectedCallback() {
    // 1. Find the main container (The Section)
    this.popup = this.closest(".not-pu-section");
    if (!this.popup) {
      console.warn("NotificationPopup: Could not find parent section .not-pu-section");
      return;
    }

    // 2. Load Settings
    this.hasSubscription = this.dataset.hasSubscription === "true";
    const parsedDelay = Number(this.dataset.delay);
    this.delay = Number.isFinite(parsedDelay) ? parsedDelay : 0;
    
    // Cookie Logic
    this.popupKey = (this.dataset.popupKey || "").trim() || this.popup.id || "default";
    this.cookieName = `hideNotificationPopup_${this.popupKey}`;

    // Editor Logic
    this.isDesignMode =
      this.dataset.designMode === "true" ||
      (window.Shopify && window.Shopify.designMode === true) ||
      document.documentElement.classList.contains("shopify-design-mode");
    this.showInEditor = this.dataset.showInEditor === "true";
    
    if (this.isDesignMode && !this.showInEditor) return;

    // 3. Check Cookies (Unless in Editor)
    if (!this.isDesignMode && (this.hasSubscription || this.getCookie(this.cookieName) === "true")) {
      return;
    }

    // 4. Bind Events
    // Close Button
    this.closeBtn = this.popup.querySelector(".newsletter__close-btn");
    if (this.closeBtn) {
      this.closeBtn.addEventListener("click", this.handleCloseClick.bind(this));
    }

    // CRITICAL FIX: Bind submit to the POPUP CONTAINER, not the form.
    // This catches the event as it bubbles up, guaranteeing interception.
    this.popup.addEventListener("submit", this.handleFormSubmit.bind(this));

    // 5. Show Popup (Timer)
    this.showTimer = window.setTimeout(() => {
      this.popup.classList.remove("hidden");
      this.popup.classList.add("wt-popup-fade-in");

      // Accessibility focus
      const input = this.popup.querySelector('input[type="email"]');
      if (input) input.focus();
    }, this.delay * 1000);
  }

  disconnectedCallback() {
    if (this.showTimer) window.clearTimeout(this.showTimer);
  }

  handleCloseClick(event) {
    event.preventDefault();
    this.popup.classList.add("hidden");
    if (!this.isDesignMode) this.setCookie(this.cookieName, "true", 7);
  }

  async handleFormSubmit(event) {
    // Check if the submit came from a form inside this popup
    const form = event.target.closest("form");
    if (!form || !this.popup.contains(form)) return;

    // --- THE INTERCEPTION ---
    // This stops the browser from reloading the page
    event.preventDefault();
    
    // This stops your Theme's other JS files from trying to take over
    event.stopImmediatePropagation(); 

    // Front-end Validation (The "Compromise" you offered, which is good practice!)
    const emailInput = form.querySelector('input[type="email"]');
    if (emailInput && !emailInput.value.includes('@')) {
       // Let the browser show its native validation bubble
       return; 
    }

    // Prevent double-submitting
    if (this.submitting) return;
    this.submitting = true;

    // UI Updates (Loading State)
    const submitBtn = form.querySelector('button[type="submit"], input[type="submit"]');
    const originalBtnText = submitBtn && submitBtn.tagName === "BUTTON" ? submitBtn.textContent : null;

    if (submitBtn) {
      submitBtn.disabled = true;
      if (submitBtn.tagName === "BUTTON") submitBtn.textContent = "Submitting…";
    }

    // Prepare Containers
    const localErrorContainer = form.querySelector(".form__message--errors") || form.querySelector(".form__message");
    const localSuccessContainer = form.querySelector(".form__message--success") || form.querySelector(".form__message");

    this.setContainerHidden(localErrorContainer, true);
    this.setContainerHidden(localSuccessContainer, true);

    try {
      const action = form.getAttribute("action") || window.location.pathname;
      const formData = new FormData(form);
      
      // AJAX Fetch
      const resp = await fetch(action, {
        method: "POST",
        body: formData,
        credentials: "same-origin",
        headers: { "X-Requested-With": "XMLHttpRequest" }
      });

      const html = await resp.text();
      const doc = new DOMParser().parseFromString(html, "text/html");

      // ID Parsing Logic
      const sectionId = this.popup.id; // Expecting: NotificationPopup-{{ section.id }}
      let scoped = sectionId ? doc.querySelector(`#${CSS.escape(sectionId)}`) : null;
      
      // Fallback: If ID not found, search the whole doc (risky but better than failing)
      if (!scoped) {
        console.warn("Popup ID not found in response. Searching global doc.");
        scoped = doc; 
      }

      const successField = scoped.querySelector(".form__success-field");
      const errorField = scoped.querySelector(".form__error-field");

      // --- SUCCESS SCENARIO ---
      if (successField) {
        if (localSuccessContainer) {
          localSuccessContainer.innerHTML = successField.outerHTML;
          this.setContainerHidden(localSuccessContainer, false);
        }

        const fieldset = form.querySelector(".wt-newsletter__fieldset");
        if (fieldset) fieldset.classList.add("hidden");

        if (!this.isDesignMode) this.setCookie(this.cookieName, "true", 30);

        setTimeout(() => {
          this.popup.classList.add("hidden");
        }, 2500);
        return;
      }

      // --- ERROR SCENARIO ---
      if (errorField) {
        if (localErrorContainer) {
          localErrorContainer.innerHTML = errorField.outerHTML;
          this.setContainerHidden(localErrorContainer, false);
        }
        return;
      }

      // --- FALLBACK (No markers found) ---
      // This usually happens if Shopify returns a Captcha challenge page
      if (localErrorContainer) {
        localErrorContainer.innerHTML = `<div class="form__error-field"><p>Please check your email and try again.</p></div>`;
        this.setContainerHidden(localErrorContainer, false);
      }

    } catch (e) {
      console.error(e);
      if (localErrorContainer) {
        localErrorContainer.innerHTML = `<div class="form__error-field"><p>Network error. Please try again.</p></div>`;
        this.setContainerHidden(localErrorContainer, false);
      }
    } finally {
      // Reset Button
      if (submitBtn) {
        submitBtn.disabled = false;
        if (submitBtn.tagName === "BUTTON" && originalBtnText != null) {
          submitBtn.textContent = originalBtnText;
        }
      }
      this.submitting = false;
    }
  }

  setContainerHidden(container, hidden) {
    if (!container) return;
    if (hidden) container.setAttribute("hidden", "hidden");
    else container.removeAttribute("hidden");
  }

  getCookie(name) {
    const cookies = document.cookie ? document.cookie.split("; ") : [];
    for (const cookie of cookies) {
      const [k, ...rest] = cookie.split("=");
      if (k === name) return decodeURIComponent(rest.join("="));
    }
    return null;
  }

  setCookie(name, value, days) {
    const date = new Date();
    date.setTime(date.getTime() + days * 24 * 60 * 60 * 1000);
    const expires = `expires=${date.toUTCString()}`;
    const secure = window.location.protocol === "https:" ? ";Secure" : "";
    document.cookie = `${name}=${encodeURIComponent(value)};${expires};path=/;SameSite=Lax${secure}`;
  }
}

if (!customElements.get("notification-popup")) {
  customElements.define("notification-popup", NotificationPopup);
}